import React from 'react';

import FolderLayout from 'Components/HumanResources/StaffFolder/FolderLayout';
import {Card, SmallLink, Pill, Meter, Paged, formatDate, plural, joinNames} from 'Components/HumanResources/StaffFolder/Ui';

/**
 * Staff folder: Overview. What needs doing, then a card for each tab.
 */
export default class Overview extends React.Component {
    /**
     * @method render
     * @return {JSX.Element}
     */
    render() {
        const userId = this.props.match.params.user;
        const base = `/admin/human-resources/staff/${userId}`;

        return (
            <FolderLayout userId={userId}>
                {({user, overview}) => {
                    const {attention, holiday, hr, forms, rates, timesheets} = overview;

                    return (
                        <div className="space-y-5">
                            <Card title="Needs your attention" id="attention">
                                {attention.length === 0 && <p className="text-gray-600">Nothing needs doing.</p>}

                                <Paged items={attention} perPage={10}>{(items) => (
                                <ul className="divide-y divide-gray-100">
                                    {items.map((item, i) => (
                                        <li key={i} className="flex flex-wrap items-center justify-between gap-3 py-3">
                                            <div>
                                                <span className="font-bold">{item.label}</span> · {item.text}
                                            </div>
                                            <SmallLink to={`${base}/${item.tab}`}>
                                                {{holiday: 'Review request', query: 'Answer query', timesheets: 'Approve entries', form: 'Go to forms'}[item.type]}
                                            </SmallLink>
                                        </li>
                                    ))}
                                </ul>
                                )}</Paged>
                            </Card>

                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                                <Card title="Holiday" id="c-hol" action={<SmallLink to={`${base}/holiday`}>Open</SmallLink>}>
                                    <div>
                                        <span className="font-league-spartan font-bold text-4xl">{holiday.remaining}</span>
                                        {' '}<span className="text-gray-600">days left of {holiday.allowance}</span>
                                    </div>
                                    <Meter value={holiday.taken} max={holiday.allowance} label={`${holiday.taken} of ${holiday.allowance} days taken`} />
                                    <div className="mt-2 text-sm">
                                        {plural(holiday.taken, 'day')} taken
                                        {holiday.pending > 0 && <> · {plural(holiday.pending, 'day')} awaiting approval</>}
                                    </div>
                                    <div className="text-sm text-gray-600">Sickness: {plural(holiday.sick_days, 'day')} this holiday year</div>
                                </Card>

                                <Card title="HR details" id="c-hr" action={<SmallLink to={`${base}/hr-details`}>Open</SmallLink>}>
                                    <ul className="space-y-1 text-sm">
                                        {hr.services.length > 0 && <li>Services: {joinNames(hr.services)}</li>}
                                        <li>{hr.start_date ? 'Started ' + formatDate(hr.start_date) : 'No start date'}</li>
                                        <li>{plural(hr.days_per_week, 'day')} a week, {plural(hr.hours_per_day, 'hour')} a day</li>
                                        <li>
                                            {hr.has_bank_details && hr.has_national_insurance ?
                                                'Bank details and NI number on file' :
                                                <Pill tone="warn">Bank details or NI number missing</Pill>}
                                        </li>
                                        <li>{hr.has_emergency_contact ? 'Emergency contact on file' : <Pill tone="warn">No emergency contact</Pill>}</li>
                                        <li>{plural(hr.documents, 'document')}</li>
                                    </ul>
                                </Card>

                                <Card title="Forms" id="c-forms" action={<SmallLink to={`${base}/forms`}>Open</SmallLink>}>
                                    <div>
                                        <span className="font-league-spartan font-bold text-4xl">{forms.complete_count}</span>
                                        {' '}<span className="text-gray-600">of {forms.total_count} staff forms complete</span>
                                    </div>
                                    <Meter value={forms.complete_count} max={forms.total_count} label={`${forms.complete_count} of ${forms.total_count} staff forms complete`} />
                                    {forms.outstanding.map(label => (
                                        <div key={label} className="mt-2 text-sm"><Pill tone="bad">Outstanding</Pill> {label}</div>
                                    ))}
                                    <div className="mt-2 text-sm text-gray-600">
                                        {plural(forms.other_forms_this_year, 'other form')} filled in this year
                                    </div>
                                </Card>

                                {window.base.features.rates && (
                                    <Card title="Rates" id="c-rates" action={<SmallLink to={`${base}/rates`}>Open</SmallLink>}>
                                        {rates.length === 0 && <p className="text-sm text-gray-600">No rates set.</p>}
                                        <ul className="space-y-1 text-sm">
                                            {rates.map(rate => (
                                                <li key={rate.name} className="flex justify-between gap-4">
                                                    <span>{rate.name}</span>
                                                    <span className="font-bold">£{Number(rate.value).toFixed(2)} an hour</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </Card>
                                )}

                                <Card title="Timesheets" id="c-ts" action={<SmallLink to={`${base}/timesheets`}>Open</SmallLink>}>
                                    <div>
                                        <span className="font-league-spartan font-bold text-4xl">{timesheets.hours}h{timesheets.mins > 0 ? ` ${timesheets.mins}m` : ''}</span>
                                        {' '}<span className="text-gray-600">worked so far in {timesheets.month_label.split(' ')[0]}</span>
                                    </div>
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {timesheets.to_approve > 0 && <Pill tone="warn">{timesheets.to_approve} to approve</Pill>}
                                        {timesheets.queried > 0 && <Pill tone="bad">{timesheets.queried} queried</Pill>}
                                        {timesheets.to_approve === 0 && timesheets.queried === 0 && <Pill tone="good">Nothing to review</Pill>}
                                    </div>
                                    <div className="mt-2 text-sm text-gray-600">
                                        {plural(timesheets.not_submitted, 'entry', 'entries')} not yet submitted
                                        {' · '}{plural(timesheets.sleep_ins, 'sleep-in')}
                                        {' · '}{timesheets.mileage} miles
                                    </div>
                                </Card>
                            </div>
                        </div>
                    );
                }}
            </FolderLayout>
        );
    }
}
